import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import ENV from 'bullitt-time-cloud-live/config/environment';

export default class WebsocketService extends Service {
  @service eventData;
  @service auth;
  @service router;

  @tracked isConnected = false;
  @tracked connectionStatus = 'disconnected';
  @tracked messages = [];
  @tracked eventId = null;

  ws = null;
  reconnectTimer = null;
  reconnectDelay = 3000; // 3 seconds
  connectionPromise = null;

  get websocketEndpoint() {
    return ENV.APP.CLOUD_WEBSOCKET_ENDPOINT;
  }

  connect(eventId) {
    // If already connected to this event, return resolved promise
    if (this.ws && this.isConnected && this.eventId === eventId) {
      console.log('[WebSocket] Already connected');
      return Promise.resolve();
    }

    // If connecting, return the pending promise
    if (this.connectionPromise) {
      console.log('[WebSocket] Connection in progress');
      return this.connectionPromise;
    }

    // Check authentication
    if (!this.auth.accessCode || !this.auth.sessionId) {
      console.log('[WebSocket] Not authenticated, cannot connect');
      this.router.transitionTo('login');
      return Promise.reject(new Error('Not authenticated'));
    }

    this.eventId = eventId;
    this.connectionStatus = 'connecting';

    // Include eventId, access code, and session ID as query parameters
    const wsUrl = `${this.websocketEndpoint}?eventId=${eventId}&code=${encodeURIComponent(this.auth.accessCode)}&sessionId=${encodeURIComponent(this.auth.sessionId)}`;
    console.log(`[WebSocket] Connecting to ${this.websocketEndpoint}?eventId=${eventId}&code=***&sessionId=***...`);

    this.connectionPromise = new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log('[WebSocket] Connected');
          this.isConnected = true;
          this.connectionStatus = 'connected';
          this.connectionPromise = null;

          // Connection is automatically subscribed to the eventId via query parameter
          console.log(`[WebSocket] Subscribed to event ${eventId}`);
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            console.log('[WebSocket] Message received:', message);

            // Add timestamp if not present
            if (!message.receivedAt) {
              message.receivedAt = new Date().toISOString();
            }

            // Keep last 100 messages
            this.messages = [message, ...this.messages].slice(0, 100);

            // Process the message through eventData service to update TrackedMaps
            this.eventData.processMessage(message);
          } catch (error) {
            console.error('[WebSocket] Error parsing message:', error);
          }
        };

        this.ws.onerror = (error) => {
          console.error('[WebSocket] Error:', error);
          this.connectionStatus = 'error';
          this.connectionPromise = null;
          reject(error);
        };

        this.ws.onclose = (event) => {
          console.log('[WebSocket] Disconnected, code:', event.code, 'reason:', event.reason);
          this.isConnected = false;
          this.connectionStatus = 'disconnected';
          this.ws = null;
          this.connectionPromise = null;

          // Check if this was an auth failure (code 4001 or 4003)
          // API Gateway returns these codes when authorizer denies access
          if (event.code === 4001 || event.code === 4003 || event.code === 1008) {
            console.log('[WebSocket] Authentication failed, redirecting to login');
            this.auth.handleAuthFailure();
            this.router.transitionTo('login');
            return;
          }

          // Auto-reconnect after delay (only if still authenticated)
          if (this.auth.accessCode && this.auth.sessionId) {
            this.scheduleReconnect();
          }
        };
      } catch (error) {
        console.error('[WebSocket] Connection error:', error);
        this.connectionStatus = 'error';
        this.connectionPromise = null;
        this.scheduleReconnect();
        reject(error);
      }
    });

    return this.connectionPromise;
  }

  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      console.log('[WebSocket] Disconnecting...');
      this.ws.close();
      this.ws = null;
    }

    this.isConnected = false;
    this.connectionStatus = 'disconnected';
  }

  send(data) {
    if (this.ws && this.isConnected) {
      console.log('[WebSocket] Sending:', data);
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('[WebSocket] Cannot send - not connected');
    }
  }

  scheduleReconnect() {
    if (this.reconnectTimer) {
      return; // Already scheduled
    }

    console.log(`[WebSocket] Reconnecting in ${this.reconnectDelay}ms...`);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.eventId) {
        this.connect(this.eventId);
      }
    }, this.reconnectDelay);
  }

  clearMessages() {
    this.messages = [];
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.disconnect();
  }
}
